import React, { useState, useEffect } from 'react';
import { oneOf, bool, string, shape } from 'prop-types';
import {
  useDataModel
  , QueryProvider
} from '@thd-nucleus/data-sources';
import { FitCompatibilityProvider, FitCompatibilityWidget } from './components';
import { FitDrawer } from './components/subcomponents';

import {
  TYPE_LIST,
  ANIMATIONS,
  IMAGES,
  LAUNDRY_ANIMATIONS,
  LAUNDRY_IMAGES,
  VALID_APPLIANCE_TYPES
} from './constants';
import { getApplianceType } from './utils';

export const FitCompatibility = ({
  navParam, itemId, type, drawerPosition, imageCacheEnabled, data: searchPropData
}) => {
  const [isLoading, setIsLoading] = useState(imageCacheEnabled);
  const [isValidApplianceType, setIsValidApplianceType] = useState(false);
  const isSearchPropValid = typeof searchPropData !== 'undefined';

  const { data: productData, loading: productLoading, error: productError } = useDataModel('product', {
    variables: {
      itemId
    },
    skip: type !== 'PIP'
  });

  const { data: searchData, loading: searchLoading, error: searchError } = useDataModel('searchModel', {
    variables: {
      navParam,
    },
    skip: type === 'PIP' || isSearchPropValid
  });

  let activeData;

  if (type === 'PIP') {
    activeData = productData;
  } else if (isSearchPropValid) {
    activeData = searchPropData;
  } else {
    activeData = searchData;
  }

  const applianceType = getApplianceType(activeData) || '';

  useEffect(() => {
    if (!productLoading && !searchLoading && activeData) {
      setIsValidApplianceType(VALID_APPLIANCE_TYPES.includes(applianceType));
    }
  }, [activeData]);

  useEffect(() => {
    const cacheImages = async () => {
      try {
        if (!TYPE_LIST.includes(type)) throw new Error('No type provided to FitCompatibility');
        await Promise.all([
          ...Object.values(ANIMATIONS),
          ...Object.values(IMAGES),
          ...Object.values(LAUNDRY_ANIMATIONS),
          ...Object.values(LAUNDRY_IMAGES)
        ].map(async (src) => {
          return new Promise((resolve, reject) => {
            const image = new Image();
            image.src = src;
            image.onload = resolve();
            image.onerror = reject();
          });
        }));
        setIsLoading(false);
      } catch (er) {
        console.log(er);
        setIsLoading(false);
      }
    };
    if (imageCacheEnabled) cacheImages();
  }, []);

  if (!TYPE_LIST.includes(type) || !isValidApplianceType) return null;

  return (
    <div
      data-testid="fit-compatibility"
      data-component="FitCompatibility"
    >
      <QueryProvider cacheKey="fit-compatibility">
        <FitCompatibilityProvider
          isLoading={isLoading}
          type={type}
          itemId={itemId}
          navParam={navParam}
          drawerPosition={drawerPosition}
          applianceType={applianceType}
        >
          <FitDrawer />
          <FitCompatibilityWidget />
        </FitCompatibilityProvider>
      </QueryProvider>
    </div>
  );
};

FitCompatibility.propTypes = {
  type: oneOf(TYPE_LIST),
  itemId: string,
  navParam: string,
  drawerPosition: oneOf(['left', 'right']),
  imageCacheEnabled: bool,
  data: shape({})
};

FitCompatibility.defaultProps = {
  itemId: '',
  navParam: '',
  type: '',
  drawerPosition: 'right',
  imageCacheEnabled: false,
  data: undefined,
};

FitCompatibility.dataModel = FitCompatibilityProvider.dataModel;

FitCompatibility.displayName = 'FitCompatibility';